/**
 * Dynamic Forms Validation Schemas
 * 
 * Zod schemas for validating form configurations and submissions
 */

import { z } from "zod";

/**
 * Field type enum
 */
export const FieldTypeSchema = z.enum([
  "text",
  "textarea",
  "number",
  "email",
  "file",
  "select",
  "multiselect",
  "date",
  "checkbox",
  "addon_select",
  "addon_multiselect",
  "integration_select",
  "integration_multiselect",
  "client_select",
  "client_multiselect",
  "item_select",
  "item_multiselect",
]);

/**
 * Form visibility enum
 */
export const FormVisibilitySchema = z.enum(["public", "private", "draft"]);

/**
 * Field option schema
 */
export const FormFieldOptionSchema = z.object({
  value: z.string(),
  label: z.string(),
});

type FormFieldOption = z.infer<typeof FormFieldOptionSchema>;

/**
 * Field validation rules schema
 */
export const FormFieldValidationSchema = z.object({
  min: z.number().optional(),
  max: z.number().optional(),
  pattern: z.string().optional(),
  maxLength: z.number().optional(),
  minLength: z.number().optional(),
});

/**
 * Entity field config schema
 */
export const EntityFieldConfigSchema = z.object({
  includeInstalledOnly: z.boolean().optional(),
});

/**
 * Item selection schema
 */
export const ItemSelectionSchema = z.object({
  type: z.enum(["addon", "integration", "client"]),
  id: z.string().min(1),
});

/**
 * Form field schema
 */
export const FormFieldSchema = z.object({
  id: z.string().min(1),
  type: FieldTypeSchema,
  label: z.string().min(1),
  description: z.string().optional(),
  required: z.boolean(),
  default: z.any().optional(),
  placeholder: z.string().optional(),
  options: z.array(FormFieldOptionSchema).optional(),
  validation: FormFieldValidationSchema.optional(),
  authRequired: z.boolean().optional(),
  entityConfig: EntityFieldConfigSchema.optional(),
});

/**
 * Complete form schema
 */
export const FormSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  description: z.string().optional(),
  visibility: FormVisibilitySchema,
  version: z.number().optional(),
  isActive: z.boolean(),
  fields: z.array(FormFieldSchema),
  createdAt: z.string(),
  updatedAt: z.string(),
  createdBy: z.string().optional(),
  updatedBy: z.string().optional(),
});

/**
 * Create form request schema
 */
export const CreateFormRequestSchema = z.object({
  id: z.string().min(1).optional(),
  name: z.string().min(1),
  description: z.string().optional(),
  visibility: FormVisibilitySchema,
  isActive: z.boolean(),
  fields: z.array(FormFieldSchema),
});

/**
 * File metadata schema
 */
export const FormFileValueSchema = z.object({
  storagePath: z.string().min(1),
  fileName: z.string().min(1),
  contentType: z.string().min(1),
  size: z.number().positive(),
});

/**
 * Submit form request schema (basic structure, detailed validation done per field)
 */
export const SubmitFormRequestSchema = z.object({
  values: z.record(z.any()),
});

/**
 * Generate upload URL request schema
 */
export const GenerateUploadUrlRequestSchema = z.object({
  fieldId: z.string().min(1),
  fileName: z.string().min(1),
  contentType: z.string().min(1),
});

/**
 * Form response context schema
 */
export const FormResponseContextSchema = z.object({
  accountId: z.string().optional(),
  userId: z.string().optional(),
  addonId: z.string().optional(),
  integrationId: z.string().optional(),
  clientId: z.string().optional(),
  subscriptionId: z.string().optional(),
  flowType: z.enum(["account_deletion", "addon_removal", "subscription_cancel", "support_contact"]).optional(),
});

/**
 * Form response schema
 */
export const FormResponseSchema = z.object({
  id: z.string().min(1),
  formId: z.string().min(1),
  submittedAt: z.string(),
  submittedBy: z.string().optional(),
  values: z.record(z.any()),
  userAgent: z.string().optional(),
  ipAddress: z.string().optional(),
  context: FormResponseContextSchema.optional(),
});

/**
 * Validates a form configuration
 */
export function validateForm(data: unknown) {
  return FormSchema.safeParse(data);
}

/**
 * Validates a create form request
 */
export function validateCreateFormRequest(data: unknown) {
  return CreateFormRequestSchema.safeParse(data);
}

/**
 * Validates a submit form request
 */
export function validateSubmitFormRequest(data: unknown) {
  return SubmitFormRequestSchema.safeParse(data);
}

/**
 * Validates a generate upload URL request
 */
export function validateGenerateUploadUrlRequest(data: unknown) {
  return GenerateUploadUrlRequestSchema.safeParse(data);
}

/**
 * Validates form field value based on field configuration
 */
export function validateFieldValue(
  field: z.infer<typeof FormFieldSchema>,
  value: unknown
): { valid: boolean; error?: string } {
  // Required field check
  if (field.required && (value === undefined || value === null || value === "")) {
    return { valid: false, error: `${field.label} is required` };
  }

  // If not required and no value, skip validation
  if (!field.required && (value === undefined || value === null || value === "")) {
    return { valid: true };
  }

  // Type-specific validation
  switch (field.type) {
    case "text":
    case "textarea":
      if (typeof value !== "string") {
        return { valid: false, error: `${field.label} must be a string` };
      }
      if (field.validation?.minLength && value.length < field.validation.minLength) {
        return {
          valid: false,
          error: `${field.label} must be at least ${field.validation.minLength} characters`,
        };
      }
      if (field.validation?.maxLength && value.length > field.validation.maxLength) {
        return {
          valid: false,
          error: `${field.label} must be at most ${field.validation.maxLength} characters`,
        };
      }
      if (field.validation?.pattern) {
        const regex = new RegExp(field.validation.pattern);
        if (!regex.test(value)) {
          return { valid: false, error: `${field.label} format is invalid` };
        }
      }
      break;

    case "email": {
      if (typeof value !== "string") {
        return { valid: false, error: `${field.label} must be a string` };
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        return { valid: false, error: `${field.label} must be a valid email` };
      }
      break;
    }

    case "number": {
      const num = typeof value === "string" ? parseFloat(value) : value;
      if (typeof num !== "number" || isNaN(num)) {
        return { valid: false, error: `${field.label} must be a number` };
      }
      if (field.validation?.min !== undefined && num < field.validation.min) {
        return {
          valid: false,
          error: `${field.label} must be at least ${field.validation.min}`,
        };
      }
      if (field.validation?.max !== undefined && num > field.validation.max) {
        return {
          valid: false,
          error: `${field.label} must be at most ${field.validation.max}`,
        };
      }
      break;
    }

    case "date": {
      if (typeof value !== "string") {
        return { valid: false, error: `${field.label} must be a date string` };
      }
      const date = new Date(value);
      if (isNaN(date.getTime())) {
        return { valid: false, error: `${field.label} must be a valid date` };
      }
      if (field.validation?.min !== undefined) {
        const minDate = new Date(field.validation.min);
        if (date < minDate) {
          return {
            valid: false,
            error: `${field.label} must be after ${minDate.toISOString()}`,
          };
        }
      }
      if (field.validation?.max !== undefined) {
        const maxDate = new Date(field.validation.max);
        if (date > maxDate) {
          return {
            valid: false,
            error: `${field.label} must be before ${maxDate.toISOString()}`,
          };
        }
      }
      break;
    }

    case "select":
      if (typeof value !== "string") {
        return { valid: false, error: `${field.label} must be a string` };
      }
      if (field.options && !field.options.some((opt: FormFieldOption) => opt.value === value)) {
        return { valid: false, error: `${field.label} has an invalid option` };
      }
      break;

    case "multiselect":
      if (!Array.isArray(value)) {
        return { valid: false, error: `${field.label} must be an array` };
      }
      if (field.options) {
        const validValues = field.options.map((opt: FormFieldOption) => opt.value);
        for (const v of value) {
          if (!validValues.includes(v)) {
            return { valid: false, error: `${field.label} has an invalid option: ${v}` };
          }
        }
      }
      break;

    case "file": {
      // File fields should contain file metadata
      const fileResult = FormFileValueSchema.safeParse(value);
      if (!fileResult.success) {
        // Could be array of files
        if (Array.isArray(value)) {
          for (const file of value) {
            const fileItemResult = FormFileValueSchema.safeParse(file);
            if (!fileItemResult.success) {
              return { valid: false, error: `${field.label} has invalid file metadata` };
            }
          }
        } else {
          return { valid: false, error: `${field.label} must contain valid file metadata` };
        }
      }
      break;
    }

    case "checkbox":
      if (typeof value !== "boolean") {
        return { valid: false, error: `${field.label} must be a boolean` };
      }
      // If required, checkbox must be true
      if (field.required && value !== true) {
        return { valid: false, error: `${field.label} must be checked` };
      }
      break;

    case "addon_select":
    case "integration_select":
    case "client_select":
      if (typeof value !== "string") {
        return { valid: false, error: `${field.label} must be a string` };
      }
      // Options should be populated at this point
      if (field.options && !field.options.some((opt: FormFieldOption) => opt.value === value)) {
        return { valid: false, error: `${field.label} has an invalid option` };
      }
      break;

    case "addon_multiselect":
    case "integration_multiselect":
    case "client_multiselect":
      if (!Array.isArray(value)) {
        return { valid: false, error: `${field.label} must be an array` };
      }
      if (field.options) {
        const validValues = field.options.map((opt: FormFieldOption) => opt.value);
        for (const v of value) {
          if (typeof v !== "string" || !validValues.includes(v)) {
            return { valid: false, error: `${field.label} has an invalid option: ${v}` };
          }
        }
      }
      break;

    case "item_select": {
      const itemResult = ItemSelectionSchema.safeParse(value);
      if (!itemResult.success) {
        return { valid: false, error: `${field.label} must be a valid item selection` };
      }
      break;
    }

    case "item_multiselect":
      if (!Array.isArray(value)) {
        return { valid: false, error: `${field.label} must be an array` };
      }
      for (const item of value) {
        const itemResult = ItemSelectionSchema.safeParse(item);
        if (!itemResult.success) {
          return { valid: false, error: `${field.label} has an invalid item selection` };
        }
      }
      break;

    default:
      return { valid: false, error: `Unknown field type: ${field.type}` };
  }

  return { valid: true };
}
